/** Repurposed as "My Training Requests" list page. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { EmptyState } from "#/components/common/EmptyState";
import { DataPagination } from "#/components/data-table/DataPagination";
import { useMyTrainingRequests } from "../hooks";
import type { TrainingRequestStatus } from "../types";

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "All statuses" },
  { value: "DRAFT",     label: "Draft" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "APPROVED",  label: "Approved" },
  { value: "REJECTED",  label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "COMPLETED", label: "Completed" },
];

const STATUS_STYLES: Record<TrainingRequestStatus, string> = {
  DRAFT:     "bg-slate-100 text-slate-700",
  SUBMITTED: "bg-amber-100 text-amber-800",
  APPROVED:  "bg-emerald-100 text-emerald-800",
  REJECTED:  "bg-red-100 text-red-700",
  CANCELLED: "bg-slate-200 text-slate-600",
  COMPLETED: "bg-indigo-100 text-indigo-800",
};

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

export default function TrainingEnrollmentsPage() {
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("");

  const query = useMyTrainingRequests(page, 10, status || undefined);

  if (query.isLoading) return <PageLoader label="Loading training requests…" />;
  if (query.isError) return <ErrorState title="Failed to load" message={String(query.error)} />;

  const data = query.data!;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Training Requests</h1>
          
        </div>
        <Link
          to="/hr/training/new"
          className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
        >
          <Plus className="h-4 w-4" /> New Request
        </Link>
      </div>

      {/* Filter */}
      <select
        className="input w-auto min-w-[180px]"
        value={status}
        onChange={(e) => { setStatus(e.target.value); setPage(0); }}
      >
        {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>

      {data.content.length === 0 ? (
        <EmptyState title="No training requests" description="Submit a new request using the button above." />
      ) : (
        <>
          <div className="rounded-2xl border bg-white shadow-sm overflow-hidden divide-y">
            {data.content.map((r) => (
              <Link
                key={r.id}
                to={`/hr/training/${r.id}`}
                className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{r.trainingTitle}</p>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {[r.providerName, r.trainingType?.replace(/_/g, " ")].filter(Boolean).join(" · ")}
                    {r.trainingStartDate ? ` · ${fmt(r.trainingStartDate)}` : ""}
                    {r.applicantEstimatedCost ? ` · ${r.currency ?? "KES"} ${r.applicantEstimatedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : ""}
                  </p>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[r.status] ?? "bg-slate-100 text-slate-600"}`}>
                  {r.status.replace(/_/g, " ")}
                </span>
              </Link>
            ))}
          </div>

          <DataPagination
            page={data.number}
            totalPages={data.totalPages}
            totalElements={data.totalElements}
            size={data.size}
            onPageChange={setPage}
            onSizeChange={() => setPage(0)}
          />
        </>
      )}
    </div>
  );
}
